import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { mailZugangQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { Button, Card, CardPad } from './ui/primitives'

/**
 * **Das Postfach, aus dem Work schreibt** (Nils, 18.08.2026: *„Ja wo soll ich die oauth2
 * sachen einstellen und die Mailadresse? Ich würde work@epcan.de anlegen."*).
 *
 * ## Drei Schritte, und die Karte führt durch sie hindurch
 *
 * 1. **Eintragen** – Absender und die drei Angaben aus der App-Registrierung in Entra.
 * 2. **Testmail an mich** – geht auch außer Betrieb. Wer prüfen will, ob der Weg steht, soll
 *    dafür nicht erst scharfschalten müssen.
 * 3. **Probebetrieb** – der Posteingang wird gelesen, ausgehende Mails gehen nur an die
 *    Probeadresse. Der Zustand zum Ausprobieren: der eingehende Weg lässt sich nur an echten
 *    Mails prüfen, und dafür musste man vorher den ausgehenden mit scharfschalten.
 * 4. **In Betrieb nehmen** – erst danach bekommt jemand anders Post.
 *
 * Der Schalter steht getrennt vom Formular, weil es zwei Entscheidungen sind: *Work kann das
 * Postfach benutzen* und *Work soll es benutzen*. Zusammengefasst wäre der erste Klick auf
 * „Speichern" gleichzeitig der Startschuss – und die Testmail käme zu spät.
 *
 * ## Warum der Schalter nicht „Versand" heißt (19.08.2026)
 *
 * Er hieß so, einen Tag lang, und Nils hat ihn deshalb aus gelassen: er wollte eine Mail
 * *ans* Postfach schicken und dachte, „Versand" ginge ihn dabei nichts an. Der Schalter
 * entscheidet aber über **beide Richtungen** – bei „aus" holt der Job auch nichts ab.
 *
 * Das ist die richtige Kopplung (ein Postfach, das Work benutzt oder nicht), aber es war die
 * falsche Beschriftung. Und weil „hinterlegt, aus" auf der Karte fast wie fertig aussieht,
 * steht jetzt ein Satz daneben, der sagt, was währenddessen **nicht** passiert.
 *
 * ## Das Geheimnisfeld ist immer leer
 *
 * Auch wenn etwas hinterlegt ist. Keine Sternchen als Platzhalter: eine Maske mit der
 * richtigen Zeichenzahl sieht aus wie ein bearbeitbarer Wert und verrät die Länge. **Leer
 * lassen heißt „unverändert"** – wer nur den Anzeigenamen korrigiert, soll nicht in Entra
 * ein neues Geheimnis erzeugen müssen.
 */
export function MailZugang() {
  const { data: zugang, error } = useQuery(mailZugangQuery)
  /**
   * **Die Felder zeigen, was gespeichert ist – bis jemand tippt** (20.08.2026).
   *
   * Vorher standen die gespeicherten Werte nur als **Platzhalter** da, die Felder selbst
   * waren leer. Wer die Probeadresse nachtragen wollte, füllte also ein Feld von fünf – und
   * „Speichern" blieb grau, weil Absender, Tenant und Client leer waren. Nils hat sie
   * zweimal eingetragen, und zweimal ist nichts angekommen; im Protokoll stand danach
   * `probe_unterdrueckt`, also *„es gibt keine Probeadresse"*. Der Fehler sah aus wie ein
   * Fehler im Versand und war einer im Formular.
   *
   * `null` heißt **unberührt** und zeigt den Wert vom Server; sobald jemand tippt, gilt das
   * Getippte. Nach dem Speichern fallen alle wieder auf `null` zurück und folgen damit dem,
   * was tatsächlich gespeichert ist – statt einer Kopie, die langsam auseinanderläuft.
   *
   * **Das Geheimnis bleibt außen vor.** Es kommt gar nicht erst vom Server, und ein leeres
   * Feld heißt dort weiterhin „unverändert".
   */
  const [eingabe, setEingabe] = useState<{
    absender?: string
    absenderName?: string
    tenantId?: string
    clientId?: string
    probeAdresse?: string
  }>({})
  const [clientSecret, setClientSecret] = useState('')

  const absender = eingabe.absender ?? zugang?.absender ?? ''
  const absenderName = eingabe.absenderName ?? zugang?.absenderName ?? ''
  const tenantId = eingabe.tenantId ?? zugang?.tenantId ?? ''
  const clientId = eingabe.clientId ?? zugang?.clientId ?? ''
  const probeAdresse = eingabe.probeAdresse ?? zugang?.probeAdresse ?? ''
  const setAbsender = (wert: string) => setEingabe((e) => ({ ...e, absender: wert }))
  const setAbsenderName = (wert: string) => setEingabe((e) => ({ ...e, absenderName: wert }))
  const setTenantId = (wert: string) => setEingabe((e) => ({ ...e, tenantId: wert }))
  const setClientId = (wert: string) => setEingabe((e) => ({ ...e, clientId: wert }))
  const setProbeAdresse = (wert: string) => setEingabe((e) => ({ ...e, probeAdresse: wert }))
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  const queryClient = useQueryClient()

  const neuLaden = () => void queryClient.invalidateQueries({ queryKey: ['mail'] })

  const speichern = useMutation({
    mutationFn: () =>
      trpc.mail.zugangSetzen.mutate({
        absender: absender.trim(),
        absenderName: absenderName.trim() || 'NetCon Work',
        tenantId: tenantId.trim(),
        clientId: clientId.trim(),
        ...(clientSecret ? { clientSecret } : {}),
        probeAdresse: probeAdresse.trim(),
      }),
    onSuccess: () => {
      setMeldung({
        art: 'ok',
        text: 'Gespeichert. Jetzt eine Testmail schicken – und danach das Postfach in Betrieb nehmen.',
      })
      /** Das Geheimnis aus dem Formularzustand entfernen, sobald es übergeben ist. */
      setClientSecret('')
      /** Und die Felder wieder dem Server folgen lassen – siehe oben. */
      setEingabe({})
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  const testen = useMutation({
    mutationFn: () => trpc.mail.testmail.mutate(),
    onSuccess: (r) => {
      setMeldung({
        art: 'ok',
        text: `Microsoft hat die Mail an ${r.an} angenommen. Sieh im Posteingang nach – und im Spam-Ordner.`,
      })
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  const umschalten = useMutation({
    mutationFn: (modus: 'aus' | 'probe' | 'betrieb') => trpc.mail.modusSetzen.mutate({ modus }),
    onSuccess: (s) => {
      setMeldung({
        art: 'ok',
        text:
          s.modus === 'betrieb'
            ? 'Postfach in Betrieb. Erwähnte bekommen ab jetzt eine Mail, und der Posteingang wird alle fünf Minuten abgeholt.'
            : s.modus === 'probe'
              ? s.probeAdresse
                ? `Probebetrieb. Der Posteingang wird gelesen; ausgehende Mails gehen an ${s.probeAdresse} statt an die Empfänger.`
                : 'Probebetrieb ohne Probeadresse. Der Posteingang wird gelesen, hinaus geht nichts – im Protokoll steht, was hinausgegangen wäre.'
              : 'Postfach außer Betrieb. Es geht nichts hinaus, und der Posteingang wird nicht gelesen.',
      })
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  const entfernen = useMutation({
    mutationFn: () => trpc.mail.zugangEntfernen.mutate(),
    onSuccess: () => {
      setMeldung({ art: 'ok', text: 'Zugang entfernt.' })
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  /** Die Abfrage ist auf `admin` beschränkt – für alle anderen gibt es die Karte nicht. */
  if (error) return null

  const laeuft = speichern.isPending || testen.isPending || umschalten.isPending
  const kannSpeichern =
    absender.trim().length > 0 &&
    tenantId.trim().length > 0 &&
    clientId.trim().length > 0 &&
    (clientSecret.length > 0 || Boolean(zugang?.gesetzt)) &&
    !laeuft

  return (
    <Card>
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-2">
          <b className="text-[13px]">Mailversand (Microsoft 365)</b>
          <span className="text-[11.5px] text-muted">
            Benachrichtigungen hinaus, Antworten und Meldungen herein
          </span>
          <span className="ml-auto text-[11.5px]">
            {!zugang?.gesetzt ? (
              <span className="text-muted">nicht eingerichtet</span>
            ) : zugang.modus === 'betrieb' ? (
              <span className="font-semibold text-accent-deep">
                in Betrieb · sendet und holt ab
              </span>
            ) : zugang.modus === 'probe' ? (
              <span className="font-semibold text-[#8a5a00]">Probebetrieb</span>
            ) : (
              <span className="text-muted">hinterlegt, außer Betrieb</span>
            )}
          </span>
        </div>

        {zugang?.gesetzt && !zugang.schluesselVorhanden && (
          <Hinweis art="fehler">
            Es ist ein Zugang hinterlegt, aber <code>CREDENTIAL_KEY</code> fehlt in der{' '}
            <code>.env</code> auf dem Server. Ohne den Schlüssel ist das Geheimnis nicht lesbar – es
            kann nichts gesendet werden.
          </Hinweis>
        )}

        {zugang?.gesetzt && (
          <div className="mt-2.5 text-[12.5px]">
            <span className="text-ink2">Absender</span>{' '}
            <strong className="font-mono">
              {zugang.absenderName} &lt;{zugang.absender}&gt;
            </strong>
            <div className="mt-0.5 text-[11.5px] text-muted">
              Tenant <span className="font-mono">{zugang.tenantId}</span> · App{' '}
              <span className="font-mono">{zugang.clientId}</span>
            </div>
            <div className="mt-0.5 text-[11.5px] text-muted">
              {zugang.letztePruefungAm
                ? `${zugang.letztePruefungOk ? '✓ ' : '✗ '}${
                    zugang.letztePruefungOk
                      ? (zugang.letztePruefungInfo ?? 'Testmail angenommen')
                      : 'letzte Testmail gescheitert'
                  } (${new Date(zugang.letztePruefungAm).toLocaleString('de-DE')})`
                : 'noch keine Testmail geschickt'}
            </div>
          </div>
        )}

        {zugang?.letzterFehler && zugang.letztePruefungOk === false && (
          <div className="mt-1.5 text-[11.5px] text-pool-sofort">{zugang.letzterFehler}</div>
        )}

        {/**
         * **Der Satz, der heute gefehlt hat.** Hinterlegt und außer Betrieb sieht auf der
         * Karte fast aus wie fertig – bis man drei Tage später sucht, warum nichts ankommt.
         */}
        {zugang?.gesetzt && zugang.modus === 'aus' && (
          <Hinweis art="fehler">
            Das Postfach ist <b>außer Betrieb</b>: es gehen keine Benachrichtigungen hinaus, und der
            Posteingang wird <b>nicht gelesen</b> – Antworten auf Benachrichtigungen und Meldungen
            an <span className="font-mono">{zugang.absender}</span> bleiben liegen. Die Testmail
            geht trotzdem, dafür ist sie da.
          </Hinweis>
        )}

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <label className="grid gap-1 text-[11.5px] font-medium text-ink2">
            Absenderadresse
            <input
              type="email"
              autoComplete="off"
              value={absender}
              onChange={(e) => setAbsender(e.target.value)}
              placeholder="netcon.work@epcan.de"
              className="rounded-lg border border-line2 px-2.5 py-1.5 font-mono text-[12px]"
            />
          </label>
          <label className="grid gap-1 text-[11.5px] font-medium text-ink2">
            Angezeigter Name
            <input
              type="text"
              value={absenderName}
              onChange={(e) => setAbsenderName(e.target.value)}
              placeholder="NetCon Work"
              className="rounded-lg border border-line2 px-2.5 py-1.5 text-[13px]"
            />
          </label>
          <label className="grid gap-1 text-[11.5px] font-medium text-ink2">
            Verzeichnis-ID (Tenant)
            <input
              type="text"
              autoComplete="off"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="rounded-lg border border-line2 px-2.5 py-1.5 font-mono text-[12px]"
            />
          </label>
          <label className="grid gap-1 text-[11.5px] font-medium text-ink2">
            Anwendungs-ID (Client)
            <input
              type="text"
              autoComplete="off"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="rounded-lg border border-line2 px-2.5 py-1.5 font-mono text-[12px]"
            />
          </label>
        </div>

        <label className="mt-2 grid gap-1 text-[11.5px] font-medium text-ink2">
          Probeadresse (für den Probebetrieb)
          <input
            type="email"
            autoComplete="off"
            value={probeAdresse}
            onChange={(e) => setProbeAdresse(e.target.value)}
            placeholder={
              zugang?.probeAdresse ?? 'deine Adresse – leer heißt: es geht nichts hinaus'
            }
            className="rounded-lg border border-line2 px-2.5 py-1.5 font-mono text-[12px]"
          />
        </label>

        <label className="mt-2 grid gap-1 text-[11.5px] font-medium text-ink2">
          Client-Geheimnis
          <input
            type="password"
            autoComplete="new-password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder={zugang?.gesetzt ? 'leer lassen heißt: unverändert' : 'Wert aus Entra'}
            className="rounded-lg border border-line2 px-2.5 py-1.5 text-[13px]"
          />
        </label>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            disabled={!kannSpeichern}
            onClick={() => speichern.mutate()}
          >
            {speichern.isPending ? 'Speichere …' : 'Speichern'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!zugang?.gesetzt || laeuft}
            onClick={() => testen.mutate()}
          >
            {testen.isPending ? 'Sende …' : 'Testmail an mich'}
          </Button>
          {/**
           * **Der Schalter heißt nicht „Versand"** (19.08.2026, aus der Benutzung gelernt).
           *
           * Er hieß so, und Nils hat ihn deshalb aus gelassen: er wollte nur eine Mail *ans*
           * Postfach schicken und dachte, „Versand" ginge ihn nichts an. Der Schalter
           * entscheidet aber über **beide Richtungen** – bei „aus" wird auch nicht abgeholt.
           * Eine Beschriftung, die die halbe Wirkung verschweigt, ist ein Fehler in der
           * Beschriftung und nicht in der Bedienung.
           */}
          {/**
           * **Drei Stufen, drei Knöpfe – und der aktuelle ist ausgegraut.**
           *
           * Ein einzelner Umschalter kann drei Zustände nicht ausdrücken, und ein Auswahlfeld
           * verschweigt, was die Stufen bedeuten. Hier steht die Reihenfolge, in der man sie
           * benutzt: erst Probe, dann Betrieb – und zurück geht es jederzeit.
           */}
          <Button
            variant={zugang?.modus === 'probe' ? 'primary' : 'ghost'}
            size="sm"
            disabled={!zugang?.gesetzt || laeuft || zugang?.modus === 'probe'}
            onClick={() => umschalten.mutate('probe')}
            title="Posteingang wird gelesen, ausgehende Mails gehen nur an die Probeadresse"
          >
            Probebetrieb
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!zugang?.gesetzt || laeuft || zugang?.modus === 'betrieb'}
            onClick={() => umschalten.mutate('betrieb')}
            title="Benachrichtigungen gehen an die echten Empfänger"
          >
            In Betrieb nehmen
          </Button>
          {zugang?.gesetzt && zugang.modus !== 'aus' && (
            <Button
              variant="ghost"
              size="sm"
              disabled={laeuft}
              onClick={() => umschalten.mutate('aus')}
            >
              Abschalten
            </Button>
          )}
          {zugang?.gesetzt && (
            <Button
              variant="ghost"
              size="sm"
              disabled={entfernen.isPending}
              onClick={() => entfernen.mutate()}
              className="ml-auto text-pool-sofort"
            >
              Zugang entfernen
            </Button>
          )}
        </div>

        {meldung && <Hinweis art={meldung.art}>{meldung.text}</Hinweis>}

        {!zugang?.gesetzt && (
          <div className="mt-3 border-t border-line pt-2 text-[11.5px] text-muted">
            <b className="text-ink2">Was vorher in Entra passieren muss:</b> eine App-Registrierung
            anlegen, ihr die <b>Anwendungsberechtigung</b>{' '}
            <span className="font-mono">Mail.Send</span> (Microsoft Graph) geben und die Zustimmung
            des Administrators erteilen. Danach ein Client-Geheimnis erzeugen – der Wert ist nur
            einmal sichtbar. Ohne eine <span className="font-mono">ApplicationAccessPolicy</span>{' '}
            darf die App in <b>jedem</b> Postfach des Mandanten schreiben; die
            Schritt-für-Schritt-Anleitung steht in{' '}
            <span className="font-mono">docs/FUER-NILS.md</span>.
          </div>
        )}
      </CardPad>
    </Card>
  )
}

function Hinweis({ art, children }: { art: 'ok' | 'fehler'; children: React.ReactNode }) {
  return (
    <div
      className={`mt-2.5 rounded-lg px-2.5 py-1.5 text-[12px] ${
        art === 'ok' ? 'bg-accent-soft text-accent-deep' : 'bg-[#fdeceb] text-pool-sofort'
      }`}
    >
      {children}
    </div>
  )
}
